import { ConfigurationItemArrayEditor } from '@client/components/_core/configuration/ConfigurationItemArrayEditor';
import { PageController } from '@client/components/_core/layout/PageController';
import { ORG_ROLES } from '@shared/constants/business/user';
import { PERMISSIONS } from '@shared/permissions/permissions';
import { COMPLIANCE_AUDIT_INTERNAL_DOMAINS_LIMITS, COMPLIANCE_AUDIT_INTERNAL_DOMAINS_SCHEMA } from '@shared/types';
import { createFileRoute } from '@tanstack/react-router';
import { ClipboardCheck } from 'lucide-react';

/**
 * Compliance-audit configuration (CUR-823). Org admins who are also compliance
 * audit managers maintain the list of bare domains that identify the
 * organisation's own email addresses. At audit run time the engine labels each
 * parent email's direction (INBOUND / OUTBOUND / INTERNAL) against this list
 * via `computeMessageDirection`; an empty resolved set means direction stays
 * UNKNOWN and the clause is suppressed from the evidence block.
 *
 * Deliberately its own setting, never shared with the auto-detect module's
 * `AUTO_DETECT_INTERNAL_EMAIL_DOMAINS` — the two are gated on different
 * modules and have separate lifecycles, so tuning the message classifier
 * must never re-label compliance-audit evidence (and vice versa).
 */
export const Route = createFileRoute('/console/$orgId/configuration/compliance/compliance-audit')({
    staticData: {
        metadata: {
            title: 'Compliance audit',
        },
        ui: {
            icon: ClipboardCheck,
            title: 'Compliance audit configuration',
            subtitle: 'Configure the internal email domains used to label email direction in audit evidence',
            menuDescription: 'Configure the internal email domains used to label email direction in audit evidence.',
        },
        featureFlag: 'enable-compliance-audit',
        modules: ['compliance-audit'],
        // The server gate needs BOTH `settings:update` and `compliance_audit:manage`.
        // A route's `permissions` list is OR, so the settings half is carried by the
        // role: `org_admin` is the only organisation role holding `settings:update`.
        roles: [ORG_ROLES.ORG_ADMIN],
        permissions: [PERMISSIONS.COMPLIANCE_AUDIT_MANAGE],
    },
    component: ComplianceAuditConfiguration,
});

// Same bare-domain rules as the auto-detect internal-domains editor, with the
// pattern compiled from the shared JSON schema so the form can never accept an
// entry the server-side schema rejects. Input is bounded by ENTRY_MAX_LENGTH,
// so the single quantified class cannot backtrack pathologically.
const INTERNAL_DOMAIN_PATTERN = new RegExp(COMPLIANCE_AUDIT_INTERNAL_DOMAINS_SCHEMA.items.pattern, 'i');

const INTERNAL_DOMAIN_RULES = [
    {
        async validator(_: unknown, value: string) {
            if (!value?.trim()) throw new Error('Domain cannot be empty');
            const trimmed = value.trim().toLowerCase();
            if (trimmed.length > COMPLIANCE_AUDIT_INTERNAL_DOMAINS_LIMITS.ENTRY_MAX_LENGTH) {
                throw new Error(
                    `Domain is too long (max ${COMPLIANCE_AUDIT_INTERNAL_DOMAINS_LIMITS.ENTRY_MAX_LENGTH} characters)`,
                );
            }
            if (trimmed.includes('@')) {
                throw new Error('Enter a bare domain (e.g. brokerco.com), not a full email address');
            }
            if (!INTERNAL_DOMAIN_PATTERN.test(trimmed)) {
                throw new Error('Must be a bare domain like brokerco.com or brand.brokerco.com');
            }
        },
    },
];

// The item rules validate a lowercased copy but leave the entered token as typed, and
// the accessor resolves the stored list case-insensitively into a Set — so the list
// itself has to reject what the entries cannot: a case-only duplicate, and a list past
// the shared schema's `maxItems`. Without these the saved value can hold `BrokerCo.com`
// beside `brokerco.com`, and more than LIST_MAX entries, that resolve to fewer domains
// than the page shows.
const INTERNAL_DOMAIN_LIST_RULES = [
    {
        async validator(_: unknown, value: string[] | undefined) {
            const entries = (value ?? []).map((entry) => entry.trim().toLowerCase());
            if (entries.length > COMPLIANCE_AUDIT_INTERNAL_DOMAINS_LIMITS.LIST_MAX) {
                throw new Error(`Add at most ${COMPLIANCE_AUDIT_INTERNAL_DOMAINS_LIMITS.LIST_MAX} domains`);
            }
            if (new Set(entries).size !== entries.length) {
                throw new Error('Domains must be unique (case is ignored)');
            }
        },
    },
];

function ComplianceAuditConfiguration() {
    return (
        <PageController route={Route}>
            {/* No `deleteOrgOverrideWhenEmpty` here, unlike the auto-detect page: saving
                zero tags persists an explicit `[]` override (direction deliberately not
                labelled), while Reset deletes the override and restores the
                authenticated-domain fallback. With the prop, clearing the list would
                silently fall back to the verified email domain — the accessor's
                "explicitly cleared" state would be unreachable from the UI. */}
            <ConfigurationItemArrayEditor
                title="Internal email domains"
                settingToEdit="COMPLIANCE_AUDIT_INTERNAL_EMAIL_DOMAINS"
                description="Bare domains (no @ or path) that identify your organisation's own email addresses — typically your brand plus any subsidiary or acquired-brand domains. When compliance audits assess whether documents were sent externally, these domains determine each email's direction (INBOUND / OUTBOUND / INTERNAL). Saving an empty list means email direction is simply not labelled in audit evidence — it implies nothing about the mail itself. Reset removes the saved list entirely, restoring the default: your organisation's verified email domain, if one is configured. Subdomain matches are recognised automatically (broking.brokerco.com matches brokerco.com)."
                itemRules={INTERNAL_DOMAIN_RULES}
                arrayRules={INTERNAL_DOMAIN_LIST_RULES}
                tagsModePlaceholder="brokerco.com, brand.brokerco.com"
                tagsModeTokenSeparators={[',', '\n']}
            />
        </PageController>
    );
}
